import { useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import {
  analyticsIdFromSearch,
  routePattern,
  scrubAnalyticsUrls,
} from '@skupreme/shared/utils/analytics';
import { CommonUtility } from '@utility/common';
import { useAuth } from './auth';

interface IntegrationProviderProps {
  children: React.ReactNode;
}

const POSTHOG_KEY: string | undefined = import.meta.env.VITE_POSTHOG_KEY;

// Production builds only (app.skupreme.com and the white-label production branches);
// staging, previews and local builds never reach the prod project.
const isProductionBuild = (): boolean =>
  typeof window !== 'undefined' && CommonUtility.isProductionEnv();

function initializePostHog() {
  if (!POSTHOG_KEY || !isProductionBuild() || posthog.__loaded) return;

  // skupreme.com runs cookieless, so its visitor id only survives the jump to
  // signup when it is passed in the URL.
  const bootstrapId = analyticsIdFromSearch(window.location.search);

  posthog.init(POSTHOG_KEY, {
    api_host: 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: 'history_change',
    session_recording: { maskAllInputs: true },
    // Unhandled errors and promise rejections become $exception events
    // (PostHog error tracking), next to the session replay they happened in.
    capture_exceptions: true,
    ...(bootstrapId && { bootstrap: { distinctID: bootstrapId } }),
    before_send: event => {
      if (!event) return event;
      // Query strings and fragments carry share tokens and sign-in codes; never record them.
      scrubAnalyticsUrls(event.properties);
      if (event.event === '$pageview') {
        const pathname =
          typeof event.properties.$pathname === 'string'
            ? event.properties.$pathname
            : window.location.pathname;
        event.properties.route_pattern = routePattern(pathname);
      }
      return event;
    },
  });
  posthog.register({ app: 'client' });
}

/** Sets up PostHog (analytics, replay and error tracking) and keeps its user
 * identity in step with who is signed in. No chat here — HubSpot's widget is retired. */
export function IntegrationProvider({ children }: IntegrationProviderProps) {
  const { isAuthenticated, user, loading } = useAuth();
  const wasIdentified = useRef(false);

  useEffect(() => {
    // No Data Collection in Dev env
    if (CommonUtility.isDevelopmentEnv()) return;
    initializePostHog();
  }, []);

  const loadIntegrationUser = () => {
    if (!user?._id) return;

    const currentOrg = user.organizations?.find(
      x => x._id === user.organization_id,
    );

    if (posthog.__loaded) {
      posthog.identify(user._id, {
        organization_id: user.organization_id,
        organization_name: currentOrg?.company_name,
      });
      wasIdentified.current = true;
    }
  };

  const unmountIntegrationUser = () => {
    // Resetting on every signed-out load would throw away the id skupreme.com
    // passed in, so only reset after a real sign-out.
    if (posthog.__loaded && wasIdentified.current) {
      posthog.reset();
      wasIdentified.current = false;
    }
  };

  useEffect(() => {
    if (loading) return;

    if (isAuthenticated) {
      loadIntegrationUser();
    } else {
      unmountIntegrationUser();
    }
  }, [isAuthenticated, loading]);

  return <>{children}</>;
}
